import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Skeleton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Divider,
  Box,
  CircularProgress,
  LinearProgress,
  Tooltip,
  Link,
} from '@mui/material';
import { X, ExternalLink, ChevronDown, ChevronUp, Info } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../services/api';
import AddFAQModal from './AddFAQModal';
import EditFAQModal from './EditFAQModal';

const stripSource = (text) => {
  if (!text) return '';
  return text.replace(/\n*\s*Source:\s*.*$/s, '').trim();
};

const getFAQInitialValues = (item) => {
  const question = item.question || '';
  const rawAnswer = item.generated_answer;
  if (!rawAnswer || rawAnswer.toLowerCase().trim() === 'na') {
    return { question, answer: '' };
  }
  return { question, answer: stripSource(rawAnswer) };
};

export default function ViewDocumentModal({ open, onClose, document }) {
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [qnaItems, setQnaItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [activeFilter, setActiveFilter] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [expandedAnswers, setExpandedAnswers] = useState({});
  const [faqInitialValues, setFaqInitialValues] = useState(null);
  const [faqSourceId, setFaqSourceId] = useState(null);
  const [addFAQOpen, setAddFAQOpen] = useState(false);
  const [faqCreatedIds, setFaqCreatedIds] = useState(new Set());
  const [editFAQOpen, setEditFAQOpen] = useState(false);
  const [editFAQData, setEditFAQData] = useState(null);
  const [editFAQLoading, setEditFAQLoading] = useState(null);
  const [faqAnswerMap, setFaqAnswerMap] = useState({});
  const sentinelRef = useRef(null);
  const PAGE_SIZE = 50;

  useEffect(() => {
    if (open && document) {
      resetAndFetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, document]);

  useEffect(() => {
    if (open && document) {
      resetAndFetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter]);

  // Coverage lives on the document detail response; fetch once per open.
  // It is supplementary, so failures are ignored silently.
  useEffect(() => {
    if (open && document) {
      setCoverage(null);
      api.admin
        .getDocument(document.id)
        .then((res) => setCoverage(res?.coverage || null))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, document]);

  const resetAndFetch = () => {
    setQnaItems([]);
    setOffset(0);
    setHasMore(true);
    setExpandedAnswers({});
    fetchQnAData(0, false);
  };

  const fetchQnAData = async (newOffset = 0, append = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    try {
      const params = { limit: PAGE_SIZE, offset: newOffset };
      if (activeFilter) params.qna_status = activeFilter;

      const response = await api.admin.getDocumentQnA(document.id, params);

      setStats(response.stats);

      const newItems = response.qna_items || [];
      if (append) {
        setQnaItems(prev => [...prev, ...newItems]);
      } else {
        setQnaItems(newItems);
      }

      setHasMore(newOffset + newItems.length < response.total);
      setOffset(newOffset + newItems.length);

      // Fetch FAQ answers for items that have linked FAQs
      const itemsWithFaq = newItems.filter(i => i.faq_id);
      if (itemsWithFaq.length > 0) {
        const results = await Promise.allSettled(
          itemsWithFaq.map(i => api.admin.getFAQ(i.faq_id))
        );
        const map = {};
        itemsWithFaq.forEach((item, idx) => {
          if (results[idx].status === 'fulfilled') {
            map[item.faq_id] = results[idx].value.answer;
          }
        });
        setFaqAnswerMap(prev => ({ ...prev, ...map }));
      }
    } catch (error) {
      console.error('Failed to fetch Q&A data:', error);
      toast.error('Failed to fetch Q&A data');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Infinite scroll via IntersectionObserver
  const handleObserver = useCallback((entries) => {
    const [entry] = entries;
    if (entry.isIntersecting && hasMore && !loading && !loadingMore) {
      fetchQnAData(offset, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, loading, loadingMore, offset, activeFilter]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: '200px',
      threshold: 0,
    });
    observer.observe(node);

    return () => observer.disconnect();
  }, [handleObserver]);

  const handleFilterClick = (filterStatus) => {
    setActiveFilter(prev => prev === filterStatus ? null : filterStatus);
  };

  const refreshStats = async () => {
    try {
      const response = await api.admin.getDocumentQnA(document.id, { limit: 1, offset: 0 });
      setStats(response.stats);
    } catch {}
  };

  const handleApprove = async (qnaId) => {
    setActionLoading(qnaId);
    try {
      await api.admin.approveQnA(document.id, qnaId);
      toast.success('Q&A approved successfully');
      setQnaItems(prev => prev.map(item =>
        item.id === qnaId ? { ...item, status: 'pass' } : item
      ));
      refreshStats();
    } catch (error) {
      console.error('Failed to approve Q&A:', error);
      toast.error('Failed to approve Q&A');
    } finally {
      setActionLoading(null);
    }
  };

  const [failConfirmItem, setFailConfirmItem] = useState(null);

  const handleFail = (qnaId) => {
    const item = qnaItems.find(i => i.id === qnaId);
    setFailConfirmItem(item || { id: qnaId });
  };

  const confirmFailOnly = async () => {
    const qnaId = failConfirmItem.id;
    setFailConfirmItem(null);
    setActionLoading(qnaId);
    try {
      await api.admin.failQnA(document.id, qnaId);
      toast.success('Q&A marked as failed');
      setQnaItems(prev => prev.map(item =>
        item.id === qnaId ? { ...item, status: 'fail' } : item
      ));
      refreshStats();
    } catch (error) {
      console.error('Failed to mark Q&A as failed:', error);
      toast.error('Failed to mark Q&A as failed');
    } finally {
      setActionLoading(null);
    }
  };

  const confirmFailAndCreateFAQ = async () => {
    const item = failConfirmItem;
    setFailConfirmItem(null);
    setActionLoading(item.id);
    try {
      await api.admin.failQnA(document.id, item.id);
      setQnaItems(prev => prev.map(i =>
        i.id === item.id ? { ...i, status: 'fail' } : i
      ));
      refreshStats();
    } catch (error) {
      console.error('Failed to mark Q&A as failed:', error);
      toast.error('Failed to mark Q&A as failed');
      setActionLoading(null);
      return;
    }
    setActionLoading(null);
    handleCreateFAQ(item);
  };

  const handleCreateFAQ = (item) => {
    setFaqInitialValues(getFAQInitialValues(item));
    setFaqSourceId(item.id);
    setAddFAQOpen(true);
  };

  const handleEditFAQ = async (item) => {
    setEditFAQLoading(item.id);
    try {
      const faq = await api.admin.getFAQ(item.faq_id);
      setEditFAQData(faq);
      setEditFAQOpen(true);
    } catch (error) {
      console.error('Failed to fetch FAQ:', error);
      toast.error('Failed to load FAQ data');
    } finally {
      setEditFAQLoading(null);
    }
  };

  const handleFAQSuccess = (createdFAQ, submittedData) => {
    const nextQuestion = createdFAQ?.question ?? submittedData?.question ?? '';
    const nextAnswer = createdFAQ?.answer ?? submittedData?.answer ?? '';

    setFaqCreatedIds(prev => new Set(prev).add(faqSourceId));
    setQnaItems(prev => prev.map(item =>
      item.id === faqSourceId
        ? {
            ...item,
            question: nextQuestion || item.question,
            generated_answer: nextAnswer || item.generated_answer,
          }
        : item
    ));
    setAddFAQOpen(false);
  };

  const toggleAnswer = (qnaId) => {
    setExpandedAnswers(prev => ({
      ...prev,
      [qnaId]: !prev[qnaId]
    }));
  };

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    const videoIdMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/);
    if (videoIdMatch) {
      return `https://www.youtube.com/embed/${videoIdMatch[1]}`;
    }
    return null;
  };

  const getWistiaEmbedUrl = (url) => {
    if (!url) return null;
    const match = url.match(/wistia\.com\/medias\/([a-zA-Z0-9]+)/);
    if (match) {
      return `https://fast.wistia.net/embed/iframe/${match[1]}`;
    }
    return null;
  };

  const isYouTubeVideo = document?.url?.includes('youtube.com') || document?.url?.includes('youtu.be');
  const isWistiaVideo = document?.url?.includes('wistia.com');
  const embedUrl = isYouTubeVideo
    ? getYouTubeEmbedUrl(document?.url)
    : isWistiaVideo
    ? getWistiaEmbedUrl(document?.url)
    : null;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  };

  const formatStatus = (status) => {
    if (!status) return 'N/A';
    // Split by underscore and convert to title case
    return status
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const getStatusColor = (status) => {
    const statusMap = {
      pass: 'success',
      pending: 'warning',
      needs_human: 'info',
      fail: 'error',
      published: 'success',
      live: 'success',
      processing: 'warning',
      qna_validating: 'warning',
      needs_review: 'info',
      failed: 'error',
      qna_pending: 'info',
    };
    return statusMap[status] || 'default';
  };

  if (!document) return null;

  // Coverage percentage drives the progress-bar color: green / amber / red.
  const coveragePct =
    coverage && coverage.content_units > 0
      ? Math.round((coverage.covered_units / coverage.content_units) * 100)
      : 0;
  const coverageBarColor =
    coveragePct >= 80 ? 'success' : coveragePct >= 50 ? 'warning' : 'error';

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        <div className="flex justify-between items-center">
          <span>View Document</span>
          <div className="flex items-center gap-2">
            {document.status && (
              <Chip
                label={formatStatus(document.status)}
                color={getStatusColor(document.status)}
                size="small"
              />
            )}
            <IconButton onClick={onClose} size="small">
              <X className="w-5 h-5" />
            </IconButton>
          </div>
        </div>
      </DialogTitle>

      <DialogContent>
        <div className="space-y-6">
          {/* Document Metadata */}
          <div>
            <Typography variant="h6" gutterBottom>
              Document Details
            </Typography>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Typography variant="body2" color="textSecondary">
                  Title
                </Typography>
                <Typography variant="body1">{document.title || 'N/A'}</Typography>
              </div>
              <div>
                <Typography variant="body2" color="textSecondary">
                  File Name
                </Typography>
                {document.url ? (
                  <Link
                    href={document.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    className="inline-flex items-center gap-1"
                  >
                    <Typography variant="body1" component="span">
                      {document.file_name || 'N/A'}
                    </Typography>
                    <ExternalLink className="w-4 h-4" />
                  </Link>
                ) : (
                  <Typography variant="body1">{document.file_name || 'N/A'}</Typography>
                )}
              </div>
              <div>
                <Typography variant="body2" color="textSecondary">
                  Created At
                </Typography>
                <Typography variant="body1">{formatDate(document.created_at)}</Typography>
              </div>
              <div>
                <Typography variant="body2" color="textSecondary">
                  Updated At
                </Typography>
                <Typography variant="body1">{formatDate(document.updated_at)}</Typography>
              </div>
            </div>
          </div>

          <Divider />

          {/* Video Preview (full width) */}
          {(isYouTubeVideo || isWistiaVideo) && embedUrl && (
            <>
              <div>
                <Typography variant="h6" gutterBottom>
                  Video Preview
                </Typography>
                <div className="aspect-video">
                  <iframe
                    width="100%"
                    height="100%"
                    src={embedUrl}
                    title={document.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>
              <Divider />
            </>
          )}

          {/* QnA Coverage */}
          {coverage && (
            <>
              <div>
                <Typography variant="h6" gutterBottom>
                  QnA Coverage
                </Typography>
                    {coverage.is_unit_tracked ? (
                      <Box>
                        <div className="flex items-center gap-2 mb-2">
                          <Typography variant="body2">
                            {coverage.covered_units} of {coverage.content_units} {coverage.unit_label}
                            {coverage.content_units === 1 ? '' : 's'} covered ({coveragePct}%)
                          </Typography>
                          <Tooltip
                            title={`Shows how many ${coverage.unit_label}s containing text had questions generated. Some ${coverage.unit_label}s — like cover pages, images, or reference/contact pages — don't produce questions, so a value under 100% can be completely normal.`}
                            arrow
                          >
                            <span className="inline-flex cursor-help text-gray-400">
                              <Info className="w-4 h-4" />
                            </span>
                          </Tooltip>
                        </div>
                        <LinearProgress
                          variant="determinate"
                          color={coverageBarColor}
                          value={Math.min(100, coveragePct)}
                          sx={{ height: 8, borderRadius: 4, mb: 2 }}
                        />
                        <div className="flex gap-2 flex-wrap">
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`Total ${coverage.unit_label}${coverage.total_units === 1 ? '' : 's'}: ${coverage.total_units}`}
                          />
                          <Chip size="small" variant="outlined" label={`With content: ${coverage.content_units}`} />
                          <Chip size="small" variant="outlined" label={`Covered: ${coverage.covered_units}`} />
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`Questions: ${coverage.questions_generated}${
                              coverage.expected_questions_upper_bound
                                ? ` / ${coverage.expected_questions_upper_bound} max`
                                : ''
                            }`}
                          />
                        </div>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        Per-{coverage.unit_label} coverage isn't available for this content (video/URL
                        transcript). Use the document status to confirm processing completed.
                      </Typography>
                    )}
              </div>

              <Divider />
            </>
          )}

          {/* Q&A Data */}
          <div>
            <Typography variant="h6" gutterBottom>
              Q&A Data
            </Typography>

            {/* Q&A Stats — clickable filters */}
            {stats && (
              <div className="flex gap-3 mb-4 flex-wrap">
                {[
                  { key: null, label: `Total: ${stats.total || 0}` },
                  { key: 'pending', label: `Pending: ${stats.pending || 0}`, color: 'warning' },
                  { key: 'pass', label: `Pass: ${stats.pass || 0}`, color: 'success' },
                  { key: 'fail', label: `Fail: ${stats.fail || 0}`, color: 'error' },
                  { key: 'needs_human', label: `Needs Human: ${stats.needs_human || 0}`, color: 'info' },
                ].map(({ key, label, color }) => (
                  <Chip
                    key={key ?? 'total'}
                    label={label}
                    color={color}
                    onClick={() => key === null ? setActiveFilter(null) : handleFilterClick(key)}
                    sx={{
                      cursor: 'pointer',
                      boxShadow: activeFilter === key ? '0 0 0 3px rgba(0,0,0,0.4)' : 'none',
                    }}
                  />
                ))}
              </div>
            )}

            {loading ? (
              <div className="space-y-2">
                <Skeleton height={40} />
                <Skeleton height={40} />
                <Skeleton height={40} />
              </div>
            ) : qnaItems.length > 0 ? (
              <>
                {/* Q&A Table */}
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell width="60"><strong>Page</strong></TableCell>
                        <TableCell width="35%"><strong>Question</strong></TableCell>
                        <TableCell width="45%"><strong>Answer</strong></TableCell>
                        <TableCell width="100"><strong>Status</strong></TableCell>
                        <TableCell width="260"><strong>Action</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {qnaItems.map((item) => {
                        const isExpanded = expandedAnswers[item.id];
                        const answerText = item.generated_answer || 'N/A';
                        const isLongAnswer = answerText.length > 150;
                        const displayText = isExpanded || !isLongAnswer
                          ? answerText
                          : `${answerText.substring(0, 150)}...`;

                        return (
                          <TableRow key={item.id} sx={{ '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.02)' } }}>
                            <TableCell sx={{ verticalAlign: 'top', paddingTop: 2 }}>
                              {item.page_number || 'N/A'}
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'top', paddingTop: 2 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {item.question}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'top', paddingTop: 2 }}>
                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    color: item.faq_id ? 'text.disabled' : 'text.secondary',
                                    lineHeight: 1.6,
                                    textDecoration: item.faq_id ? 'line-through' : 'none',
                                  }}
                                >
                                  {displayText}
                                </Typography>
                                {isLongAnswer && (
                                  <Button
                                    size="small"
                                    onClick={() => toggleAnswer(item.id)}
                                    sx={{
                                      mt: 1,
                                      textTransform: 'none',
                                      minWidth: 'auto',
                                      padding: '2px 8px'
                                    }}
                                    endIcon={isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                  >
                                    {isExpanded ? 'Show less' : 'Show more'}
                                  </Button>
                                )}
                                {item.faq_id && faqAnswerMap[item.faq_id] && (
                                  <Box sx={{ mt: 1, borderLeft: '3px solid', borderColor: 'primary.main', pl: 1 }}>
                                    <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600, display: 'block' }}>
                                      FAQ Answer
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        color: 'text.primary',
                                        lineHeight: 1.6,
                                      }}
                                    >
                                      {faqAnswerMap[item.faq_id]}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'top', paddingTop: 2 }}>
                              <Chip
                                label={formatStatus(item.status)}
                                color={getStatusColor(item.status)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'top', paddingTop: 2, whiteSpace: 'nowrap' }}>
                              <div className="flex gap-2">
                                <Button
                                  variant="outlined"
                                  size="small"
                                  color="success"
                                  onClick={() => handleApprove(item.id)}
                                  disabled={actionLoading === item.id || item.status === 'pass'}
                                >
                                  {actionLoading === item.id ? 'Loading...' : 'Approve'}
                                </Button>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  color="error"
                                  onClick={() => handleFail(item.id)}
                                  disabled={actionLoading === item.id || item.status === 'fail'}
                                >
                                  {actionLoading === item.id ? 'Loading...' : 'Fail'}
                                </Button>
                                {item.faq_id ? (
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    color="primary"
                                    onClick={() => handleEditFAQ(item)}
                                    disabled={editFAQLoading === item.id}
                                  >
                                    {editFAQLoading === item.id ? 'Loading...' : 'Edit FAQ'}
                                  </Button>
                                ) : (
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    color="primary"
                                    onClick={() => handleCreateFAQ(item)}
                                    disabled={!['fail', 'needs_human'].includes(item.status) || faqCreatedIds.has(item.id)}
                                  >
                                    Create FAQ
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Infinite scroll sentinel */}
                <div ref={sentinelRef} className="flex justify-center py-4">
                  {loadingMore && <CircularProgress size={28} />}
                  {!hasMore && qnaItems.length > PAGE_SIZE && (
                    <Typography variant="body2" color="textSecondary">
                      All {qnaItems.length} items loaded
                    </Typography>
                  )}
                </div>
              </>
            ) : (
              <Typography variant="body2" color="textSecondary">
                {activeFilter ? `No Q&A items with status "${formatStatus(activeFilter)}"` : 'No Q&A data available'}
              </Typography>
            )}
          </div>
        </div>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {/* Fail confirmation dialog */}
      <Dialog
        open={!!failConfirmItem}
        onClose={() => setFailConfirmItem(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Mark as Failed</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Would you like to create an FAQ for this question before marking it as failed?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFailConfirmItem(null)}>Cancel</Button>
          <Button onClick={confirmFailOnly} color="error" variant="outlined">
            No, Just Fail
          </Button>
          <Button onClick={confirmFailAndCreateFAQ} color="primary" variant="contained">
            Yes, Create FAQ
          </Button>
        </DialogActions>
      </Dialog>

      <AddFAQModal
        open={addFAQOpen}
        onClose={() => setAddFAQOpen(false)}
        onSuccess={handleFAQSuccess}
        initialValues={faqInitialValues}
        qnaId={faqSourceId}
      />

      <EditFAQModal
        open={editFAQOpen}
        onClose={() => { setEditFAQOpen(false); setEditFAQData(null); }}
        faq={editFAQData}
        onSuccess={(updatedFaq) => {
          if (updatedFaq?.id && updatedFaq?.answer !== undefined) {
            setFaqAnswerMap(prev => ({ ...prev, [updatedFaq.id]: updatedFaq.answer }));
          }
          setEditFAQOpen(false);
          setEditFAQData(null);
        }}
      />
    </Dialog>
  );
}
